import { useEffect, useMemo, useRef, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  type DialpadActive,
  type DialpadPreview,
  type DialpadUsersResponse,
  type UserOut,
} from "../lib/api";
import {
  fmtDuration,
  fmtRange,
  presetRange,
  type RangePreset,
} from "../lib/format";
import { activePollMs, isSyncStuck, syncPollMs } from "../lib/polling";
import { MinDurationSetting } from "./dialpad/shared";
import { DialpadUsersSection } from "./dialpad/DialpadUsersSection";
import { Phase3AssessmentsCard } from "./dialpad/Phase3AssessmentsCard";
import { DeleteResultsCard } from "./dialpad/DeleteResultsCard";
import { RetentionCard } from "./dialpad/RetentionCard";

// Wave 9 — super-admin-only "Data" view. Houses the heavier data tools
// that used to live inline on the Dialpad view: the manual Advanced Sync
// Call Data card, Dialpad Users, the Phase 3 Assessment Runner, and the
// Delete Assessment Results dev tool. Triggering a sync here mutates the
// same shared server state (dialpad-active / dialpad-windows /
// dialpad-coverage React Query keys) that the Dialpad view reads, so live
// progress and run history still surface there.
export function DataView({ me }: { me: UserOut }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<RangePreset>("24h");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  // Snapshot "now" only when preset/custom inputs change so re-renders don't
  // drift the range and trigger an infinite preview loop.
  const range = useMemo(
    () => presetRange(preset, customStart, customEnd),
    [preset, customStart, customEnd],
  );
  const rangeKey = range
    ? `${range.start.toISOString()}|${range.end.toISOString()}`
    : "";
  const [previewData, setPreviewData] = useState<DialpadPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Task #359 — when true, all sync/phase buttons send ``force=true``
  // so previously-fetched data is refreshed instead of skipped.
  const [forceMode, setForceMode] = useState(false);

  const previewM = useMutation({
    mutationFn: async () => {
      if (!range) throw new Error("Pick a start and end");
      return api.dialpadPreview(
        range.start.toISOString(),
        range.end.toISOString(),
      );
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setPreviewError(null);
    },
    onError: (e: { message?: string }) => {
      setPreviewData(null);
      setPreviewError(e.message || "Preview failed");
    },
  });

  // Auto-preview when range changes
  useEffect(() => {
    if (!range) {
      setPreviewData(null);
      return;
    }
    previewM.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  // Shared active-sync state (same React Query key the Dialpad view polls)
  // so the Sync buttons disable while a sync is running — the in-progress
  // panel and run history still render on the Dialpad view.
  const activeQ = useQuery<DialpadActive>({
    queryKey: ["dialpad-active"],
    queryFn: () => api.dialpadActive(),
    refetchInterval: (q) => activePollMs(q.state.data?.has_active),
    placeholderData: keepPreviousData,
  });
  const hasActive = activeQ.data?.has_active;

  const ingestM = useMutation({
    mutationFn: async (
      opts: {
        force?: boolean;
        phases?: "all" | "phase1" | "phase2" | "phase3";
      } = {},
    ) => {
      if (!range) throw new Error("Pick a range");
      return api.dialpadIngest(
        range.start.toISOString(),
        range.end.toISOString(),
        opts.force ?? false,
        opts.phases ?? "all",
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-active"] });
      qc.invalidateQueries({ queryKey: ["dialpad-windows"] });
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
      qc.invalidateQueries({ queryKey: ["dialpad-users"] });
      previewM.mutate();
    },
  });
  const ingestErr = ingestM.error as { message?: string } | null;

  // When an ingest/backfill transitions from running to idle, refresh the
  // gap preview and invalidate the shared server-state keys so this card
  // (and the Dialpad view's tiles, on next mount) reflect the
  // freshly-ingested data. Relocated with the sync card from DialpadView.
  const prevHasActiveRef = useRef(false);
  useEffect(() => {
    const running = !!activeQ.data?.has_active;
    if (prevHasActiveRef.current && !running) {
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
      qc.invalidateQueries({ queryKey: ["dialpad-users"] });
      qc.invalidateQueries({ queryKey: ["dialpad-windows"] });
      previewM.mutate();
    }
    prevHasActiveRef.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQ.data?.has_active, qc]);

  const syncDisabled =
    !range ||
    !previewData ||
    // PET-668 — force mode overrides the "already fully covered" guard so a
    // supadmin can wipe + rebuild a covered window. Every other guard
    // (no range, ingest in progress, request pending) still applies.
    (previewData.fully_covered && !forceMode) ||
    hasActive ||
    ingestM.isPending;

  // ---------- Dialpad Users ----------
  const usersQ = useQuery<DialpadUsersResponse>({
    queryKey: ["dialpad-users"],
    queryFn: () => api.dialpadUsers(),
    placeholderData: keepPreviousData,
  });

  const syncUsersM = useMutation({
    mutationFn: () => api.dialpadSyncUsers(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-sync-users-active"] });
      // Belt-and-suspenders: if a very fast sync completes between
      // active-state polls, invalidate the users list shortly after so
      // the table is guaranteed to refresh even if we never observed
      // has_active=true.
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["dialpad-users"] });
        qc.invalidateQueries({ queryKey: ["dialpad-sync-users-active"] });
      }, 5000);
    },
  });
  const syncUsersErr = syncUsersM.error as { message?: string } | null;

  const syncUsersActiveQ = useQuery({
    queryKey: ["dialpad-sync-users-active"],
    queryFn: () => api.dialpadSyncUsersActive(),
    // Poll fast while running, but back off once the sync looks stuck (worker
    // down) so we don't keep hitting the API every 2s and trip the rate limit.
    refetchInterval: (q) =>
      syncPollMs(q.state.data?.has_active, q.state.data?.queued_at, Date.now()),
    placeholderData: keepPreviousData,
  });
  const usersSyncRunning = !!syncUsersActiveQ.data?.has_active;
  // A sync that has been queued/running far longer than normal almost always
  // means the worker isn't processing jobs — surface that instead of leaving
  // the button stuck on "Queuing…" forever.
  const usersSyncStuck = isSyncStuck(
    usersSyncRunning,
    syncUsersActiveQ.data?.queued_at,
    Date.now(),
  );
  // A job that died inside the worker reports has_active=false; surface its
  // error so the failure isn't silent.
  const usersSyncFailed = !usersSyncRunning && !!syncUsersActiveQ.data?.failed;
  const usersSyncJobError = usersSyncFailed
    ? (syncUsersActiveQ.data?.error ?? null)
    : null;
  // When a sync_users job transitions from active -> inactive, refetch
  // the users list so the table reflects the freshly synced data.
  const prevUsersRunningRef = useRef(false);
  useEffect(() => {
    if (prevUsersRunningRef.current && !usersSyncRunning) {
      qc.invalidateQueries({ queryKey: ["dialpad-users"] });
      // Clear stale mutation success state so inline status does not
      // linger on "Users sync queued" after the job has actually finished.
      syncUsersM.reset();
    }
    prevUsersRunningRef.current = usersSyncRunning;
  }, [usersSyncRunning, qc, syncUsersM]);

  // ---------- Ticket 8 — Phase 3 (LLM-graded assessment runs) ----------
  const assessmentRunActiveQ = useQuery({
    queryKey: ["assessment-run-active"],
    queryFn: () => api.assessmentRunsActive(),
    refetchInterval: (q) => activePollMs(q.state.data?.has_active),
    placeholderData: keepPreviousData,
    enabled: me.role === "supadmin" || me.role === "admin",
  });
  const assessmentRunRunning = !!assessmentRunActiveQ.data?.has_active;

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-2xl font-semibold text-foreground"
          style={{ fontFamily: "var(--app-font-serif)" }}
        >
          Data
        </h2>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-md flex flex-col">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Dialpad</div>
        <div className="font-medium text-foreground mt-1">Advanced Sync Call Data</div>
        <p className="text-sm text-muted-foreground mt-1">
          Pick a time range. We'll show you what's already synced and only
          fetch the gap. Dialpad users and groups are refreshed
          automatically at the start of every sync.
        </p>

        <div className="mt-3 pt-3 border-t border-border">
          <MinDurationSetting />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {(["24h", "7d", "30d", "custom"] as RangePreset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPreset(p)}
              className={`rounded px-3 py-1.5 text-sm border ${
                preset === p
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-foreground border-input hover:bg-muted/40"
              }`}
            >
              {p === "24h"
                ? "Last 24 hours"
                : p === "7d"
                ? "Last 7 days"
                : p === "30d"
                ? "Last 30 days"
                : "Custom range"}
            </button>
          ))}
        </div>

        {preset === "custom" && (
          <div className="mt-3 flex flex-wrap gap-3">
            <label className="flex flex-col text-xs text-muted-foreground">
              Start
              <input
                type="datetime-local"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border border-input rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="flex flex-col text-xs text-muted-foreground">
              End
              <input
                type="datetime-local"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border border-input rounded px-2 py-1.5 text-sm"
              />
            </label>
          </div>
        )}

        <div className="mt-4 rounded border border-border bg-muted/40 p-3 text-sm">
          {previewError ? (
            <div className="text-destructive">{previewError}</div>
          ) : !range ? (
            <div className="text-muted-foreground">
              Pick start and end times to see what needs syncing.
            </div>
          ) : !previewData ? (
            <div className="text-muted-foreground">Checking what you already have…</div>
          ) : previewData.fully_covered ? (
            forceMode ? (
              <div className="text-foreground">
                You already have everything for{" "}
                {fmtRange(
                  previewData.requested.start,
                  previewData.requested.end,
                )}
                .{" "}
                <span className="font-medium">Re-sync (force) is on</span>, so
                Sync will wipe the existing data for this window and rebuild
                it from scratch.
              </div>
            ) : (
              <div className="text-primary">
                You already have everything for{" "}
                {fmtRange(previewData.requested.start, previewData.requested.end)}.
                Nothing to sync.
              </div>
            )
          ) : previewData.already_covered.length === 0 ? (
            <div className="text-foreground">
              No prior syncs in this range. Will fetch the full window (
              {fmtDuration(
                previewData.requested.start,
                previewData.requested.end,
              )}
              ).
            </div>
          ) : (
            <div className="text-foreground space-y-1">
              <div>
                <span className="font-medium">You already have</span>{" "}
                {previewData.already_covered.length} window
                {previewData.already_covered.length === 1 ? "" : "s"} in this
                range.
              </div>
              <div>
                <span className="font-medium">Will fetch:</span>
                <ul className="list-disc ml-5 mt-1 text-xs">
                  {previewData.gaps.map((g, i) => (
                    <li key={i}>
                      {fmtRange(g.start, g.end)}{" "}
                      <span className="text-muted-foreground">
                        ({fmtDuration(g.start, g.end)})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => ingestM.mutate({ force: forceMode })}
            disabled={syncDisabled}
            className="rounded bg-primary text-primary-foreground text-sm px-4 py-2 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          >
            {ingestM.isPending
              ? "Queuing…"
              : hasActive
              ? "Sync in progress…"
              : "Sync"}
          </button>
          <button
            type="button"
            title="Run only Phase 1 (calls metadata). Phase 0 (users + groups) always runs first as a prerequisite."
            onClick={() =>
              ingestM.mutate({ force: forceMode, phases: "phase1" })
            }
            disabled={!range || hasActive || ingestM.isPending}
            className="rounded border border-input bg-card text-foreground text-sm px-3 py-2 hover:bg-muted/40 disabled:opacity-50"
          >
            Phase 1
          </button>
          <button
            type="button"
            title="Run only Phase 2 (transcripts + recaps) for the calls already ingested in this range."
            onClick={() =>
              ingestM.mutate({ force: forceMode, phases: "phase2" })
            }
            disabled={!range || hasActive || ingestM.isPending}
            className="rounded border border-input bg-card text-foreground text-sm px-3 py-2 hover:bg-muted/40 disabled:opacity-50"
          >
            Phase 2
          </button>
          <button
            type="button"
            title="Run only Phase 3 (assessments) for this range."
            onClick={() =>
              ingestM.mutate({ force: forceMode, phases: "phase3" })
            }
            disabled={!range || hasActive || ingestM.isPending}
            className="rounded border border-input bg-card text-foreground text-sm px-3 py-2 hover:bg-muted/40 disabled:opacity-50"
          >
            Phase 3
          </button>
          <label
            className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
            title="When on, the next Sync / Phase button press re-processes data that was already fetched (instead of skipping it). Useful after changing settings."
          >
            <input
              type="checkbox"
              checked={forceMode}
              onChange={(e) => setForceMode(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Re-sync (force)
          </label>
          {ingestErr && (
            <span className="text-xs text-destructive">{ingestErr.message}</span>
          )}
        </div>
      </div>

      <DialpadUsersSection
        usersQ={usersQ}
        onSync={() => syncUsersM.mutate()}
        syncing={syncUsersM.isPending || usersSyncRunning}
        syncError={syncUsersErr?.message ?? usersSyncJobError}
        stuck={usersSyncStuck}
      />

      <Phase3AssessmentsCard autoRunInFlight={assessmentRunRunning} />

      {/* ADR 0036 — sits beside the manual delete card because both are about
          data leaving the system; this one is the automatic, scheduled half. */}
      <RetentionCard />

      {/* Kept inline supadmin gate — defense in depth on top of the
          view-level gate (nav visibility + App.tsx deep-link bounce). */}
      {me.role === "supadmin" && <DeleteResultsCard defaultRange={range} />}
    </div>
  );
}
